import React from "react";
import PropTypes from "prop-types";
import Wizard from "common/components/Wizard";
import NameStep from "./Steps/nameStep";
import InfoStep from "./Steps/infoStep";
import { name, info, type } from "core/Offers/validations";
import { get, pick } from "lodash";
import { withStyles } from "@mui/styles";
import TypeStep from "./Steps/typeStep";
import moment from "moment";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { urlResource } from "common/utils";
import client from "common/admin/client";
import { getResourceCity } from "common/admin/operations";
import { connect } from "react-redux";
import { compose } from "redux";
import { currentUserNameSelector } from "common/auth/selectors";
import ErrorPage from "common/components/ErrorPage";
import { Translation } from "react-i18next";
import { getItem, localStorage } from "common/storage";
import { AppValues, LANG_CODES_KEYS, TARGET_PREFIXES } from "common/constants";

const fields = [
  "name",
  "custom_values",
  "short_description",
  "long_description",
  "legal",
  "quantity",
  "start_at",
  "end_at",
  "target",
  "defaultTerms",
];

const getInitialValues = () => [
  {
    name: "",
  },
  {
    offerType: "",
  },
  {
    quantity: "",
    target: "",
    start_at: moment().add(5, "m").toDate(),
    end_at: moment().add(10, "m").toDate(),
    offer_template: {
      id: "",
      template_options: [],
    },
    short_description: "",
    long_description: "",
    custom_values: {
      getValue: "",
      dollarValue: "",
      eventType: "",
      percentValue: "",
      buyValue: "",
    },
    fullDay: false,
    legal: "",
    defaultTerms: true,
    hero: "",
  },
];

class CreateOffer extends React.Component {
  state = {
    city: null,
    loaded: false,
    error: null,
    eventTargets: [],
    groupTargets: [],
  };

  isValidUUID(uuid) {
    try {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(uuid);
    } catch (_) {
      return false;
    }
  }

  handleSubmit = async (values, form) => {
    const resource = urlResource(this.props.match.path);
    const apiUrl = `${resource.apiBase}/offers`;
    const { setSubmitting, setErrors } = form;
    const multilingual = getItem(localStorage, AppValues.multilingual) || false;
    const { defaultTerms, ...formValues } = pick(values, fields);
    const body = {
      ...formValues,
      offer_template: values.offer_template.id,
      multilingual: multilingual,
      legal_default: defaultTerms,
    };

    const selectedTarget = `${values.target || ""}`;

    if (selectedTarget.startsWith(TARGET_PREFIXES.event)) {
      const eventId = selectedTarget.slice(TARGET_PREFIXES.event.length);
      if (this.isValidUUID(eventId)) {
        body.event_id = eventId;
        body.target = "event";
      }
    } else if (selectedTarget.startsWith(TARGET_PREFIXES.group)) {
      const groupId = parseInt(
        selectedTarget.slice(TARGET_PREFIXES.group.length),
        10,
      );
      if (groupId > 0) {
        body.building_group_id = groupId;
        body.target = "group";
      }
    } else if (selectedTarget === "building") {
      body.target = "building";
    } else if (selectedTarget) {
      // Backward compatibility for older persisted/untyped target values.
      if (this.isValidUUID(selectedTarget)) {
        body.event_id = selectedTarget;
        body.target = "event";
      } else if (parseInt(selectedTarget, 10) > 0) {
        body.building_group_id = parseInt(selectedTarget, 10);
        body.target = "group";
      } else {
        body.target = selectedTarget;
      }
    }

    try {
      const offer = await client.post(apiUrl, { body });

      if (values.hero) {
        const response = await client.postFile(
          "offers",
          offer.id,
          "hero",
          values.hero,
        );

        await client.patch(`${apiUrl}/${offer.id}`, {
          body: {
            hero_url: response.url,
          },
        });
      }

      this.setState({ success: true });
    } catch (e) {
      setErrors({ http: e.message });
    }

    setSubmitting(false);
  };

  async componentDidMount() {
    const { resource } = this.props;

    try {
      let eventTargets = [];
      let groupTargets = [];

      if (resource.plural && resource.plural === "buildings" && resource.id) {
        const activeNotifications = await client.get(
          `buildings/${resource.id}/notifications`,
        );
        if (activeNotifications && activeNotifications.length > 0) {
          eventTargets = activeNotifications
            .filter(
              (x) =>
                x.event_id &&
                !x.is_contest &&
                //moment(x.available_at).isSameOrBefore(moment()) &&
                moment(x.available_until).isAfter(moment()),
            )
            .map((notification) => ({
              value: `${TARGET_PREFIXES.event}${notification.event_id}`,
              label: notification.title,
            }));
        }
      }

      if (resource.plural && resource.plural === "buildings" && resource.id) {
        const activeBuildingGroups = await client.get(
          `buildings/${resource.id}/groups`,
        );
        const lang = getItem(localStorage, LANG_CODES_KEYS.i18nLang) || "en";
        if (
          activeBuildingGroups &&
          activeBuildingGroups.groups &&
          activeBuildingGroups.groups.length > 0
        ) {
          groupTargets = activeBuildingGroups.groups.map((group) => ({
            value: `${TARGET_PREFIXES.group}${group.group_id}`,
            label: lang === "en" ? group.group_name : group.group_name_fr,
          }));
        }
      }

      this.setState({ eventTargets, groupTargets });
      const city = await getResourceCity(resource.plural, resource.id);
      this.setState({ city, loaded: true });
    } catch (error) {
      this.setState({ error, loaded: true });
    }
  }

  handleClose = (history) => () => {
    const { resource } = this.props;
    this.setState({ success: false });
    history.push(`/${resource.singular}/${resource.id}`);
  };

  handleCancel = (history) => () => {
    const { resource } = this.props;
    history.push(`/${resource.singular}/${resource.id}`);
  };

  render() {
    const { classes, history, username } = this.props;
    const { city, loaded, error, eventTargets, groupTargets } = this.state;

    if (!loaded) {
      return <CircularProgress />;
    }

    if (error) {
      return <ErrorPage />;
    }

    return (
      <Translation>
        {(t) => (
          <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={1}
            className={classes.container}
          >
            <Grid xs>
              <Typography
                paragraph
                variant="h5"
                className={classes.headline}
                component="h1"
              >
                {t("offer_create.greet_title", { name: username })}
              </Typography>
              {/* <Typography variant="caption" paragraph>
                {t("offer_create.greet_subtitle")}
              </Typography> */}
            </Grid>
            <Grid xs>
              <Wizard
                initialValues={getInitialValues()}
                onSubmit={this.handleSubmit}
                onCancel={this.handleCancel(history)}
              >
                <NameStep schema={name(t)} />
                <TypeStep schema={type(t)} />
                <InfoStep
                  eventTargets={eventTargets}
                  groupTargets={groupTargets}
                  timezone={get(city, "timezone_name")}
                  schema={info(t)}
                />
              </Wizard>
            </Grid>
            <Dialog open={get(this.state, "success", false)} keepMounted>
              <DialogTitle id="alert-dialog-slide-title">
                {t("offer_create.submitted_popup_title")}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                  {t("offer_create.submitted_popup_content")}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button color="primary" onClick={this.handleClose(history)}>
                  {t("offer_create.submitted_popup_okay")}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        )}
      </Translation>
    );
  }
}

CreateOffer.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  resource: PropTypes.shape({
    id: PropTypes.number.isRequired,
    plural: PropTypes.string.isRequired,
    singular: PropTypes.string.isRequired,
    apiBase: PropTypes.string.isRequired,
  }).isRequired,
};

const styles = (theme) => ({
  headline: {
    marginBottom: theme.spacing(2),
  },
  container: {
    marginLeft: theme.spacing(3),
  },
});

const mapStateToProps = (state, ownProps) => ({
  username: currentUserNameSelector(state),
  resource: urlResource(ownProps.match.path),
});

const enhanced = compose(withStyles(styles), connect(mapStateToProps));

export default enhanced(CreateOffer);
